import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { DatePreset } from '@tekansa/core/analytics';
import { api, call } from '../client';

/* Step 16b: the Home page (6 cards, 2 charts, 3 small tables, attention; Sales Rep version). */

export interface HomeQuery {
  preset: DatePreset;
  from?: string | undefined;
  to?: string | undefined;
  campaignId?: string | undefined;
  domainId?: string | undefined;
  mailboxId?: string | undefined;
}

export const fetchHome = (ws: string, q: HomeQuery) =>
  call(
    api.GET('/v1/workspaces/{workspaceId}/home', {
      params: {
        path: { workspaceId: ws },
        query: {
          preset: q.preset,
          ...(q.preset === 'custom' && q.from && q.to ? { from: q.from, to: q.to } : {}),
          ...(q.campaignId ? { campaignId: q.campaignId } : {}),
          ...(q.domainId ? { domainId: q.domainId } : {}),
          ...(q.mailboxId ? { mailboxId: q.mailboxId } : {}),
        },
      },
    }),
  );

export type HomeData = Awaited<ReturnType<typeof fetchHome>>;
export type HomeTeam = NonNullable<HomeData['team']>;
export type HomeRep = NonNullable<HomeData['rep']>;
export type AttentionItem = HomeData['attention'][number];

/** Under the "analytics" key, so a goal change refreshes it too. */
export function useHome(ws: string | null, q: HomeQuery) {
  return useQuery({
    queryKey: ['analytics', 'home', ws ?? 'none', q],
    queryFn: () => fetchHome(ws ?? '', q),
    enabled: ws !== null && (q.preset !== 'custom' || Boolean(q.from && q.to)),
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });
}

export const fetchAttention = (ws: string) =>
  call(
    api.GET('/v1/workspaces/{workspaceId}/attention', {
      params: { path: { workspaceId: ws } },
    }),
  );

/** Step 17c: the top bar's "Things to fix" (checked every minute and when you come back). */
export function useAttention(ws: string | null, enabled: boolean) {
  return useQuery({
    queryKey: ['attention', ws ?? 'none'],
    queryFn: () => fetchAttention(ws ?? ''),
    enabled: enabled && ws !== null,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  });
}
